// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Invite deep-link params (`?email=&orgSlug=`). The invite link lands on `/`, which
// the router immediately redirects (/ → /dashboard → /login for an unauthenticated
// visitor) — and each client-side redirect drops the query string. So we must read
// the params SYNCHRONOUSLY at app entry (main.tsx, before the router renders) and
// stash them in sessionStorage; LoginPage reads them back after it finally mounts.
//
// These are non-PHI session metadata (the invitee's own work email + an org slug),
// tab-scoped, and consumed one-shot by LoginPage.

const EMAIL_KEY = 'qh.inviteEmail'
const SLUG_KEY = 'qh.inviteOrgSlug'

/**
 * Read `?email=&orgSlug=` from the current URL and stash any present values.
 * Call once at app entry, before the router can redirect the query away. Only
 * writes values that are actually present, so it never clobbers a captured invite
 * with empties on the federated-redirect return (whose URL has no invite params).
 */
export function captureInviteParamsFromUrl(): void {
  try {
    const p = new URLSearchParams(window.location.search)
    const email = p.get('email')?.trim()
    const orgSlug = p.get('orgSlug')?.trim()
    if (email) sessionStorage.setItem(EMAIL_KEY, email)
    if (orgSlug) sessionStorage.setItem(SLUG_KEY, orgSlug)
  } catch {
    // sessionStorage/URL unavailable — invite prefill simply won't apply.
  }
}

export function readInviteParams(): { email: string; orgSlug: string } {
  try {
    return {
      email: sessionStorage.getItem(EMAIL_KEY) ?? '',
      orgSlug: sessionStorage.getItem(SLUG_KEY) ?? '',
    }
  } catch {
    return { email: '', orgSlug: '' }
  }
}

/** Drop the stashed invite params once LoginPage has consumed them (one-shot). */
export function clearInviteParams(): void {
  try {
    sessionStorage.removeItem(EMAIL_KEY)
    sessionStorage.removeItem(SLUG_KEY)
  } catch {
    // ignore
  }
}
